// OnshapeView — "Onshape" main bucket. Spike_OnshapeBucket_Brief.md +
// Spike_OnshapeBucket2_Brief.md (Alex's 3 comments) + UI_VISION §8.0 (the Main).
//
// Left tree  = the REAL Catalog + Product hierarchies (same L1▸L2▸L3(▸item) levels
//              as the Catalog/Products screens — see CatalogView's `/api/catalog/tree`
//              and ProductsView's `categoryTree` / `/api/products/tree`), composed via
//              tree-model.js's composeTree() — the ONE composition function every
//              screen's tree derives from (Alex, 2026-07-20: "the tree used in
//              Onshape [should] be the leader"). Here it runs in FULL: both
//              sources, pruned to only the branches with an Onshape-linked item,
//              namespaced (`p:`/`c:`) so the two id spaces can't collide. A linked
//              leaf = a product with onshape_ref (product tree) / a catalog FAMILY
//              with onshape_ref (catalog tree) — matches GET /api/onshape/linked's
//              two lists. Ancestors of a kept leaf are kept; branches with nothing
//              linked underneath are dropped. Catalog/Products call the SAME
//              composeTree() in degenerate single-source form (a passthrough —
//              see tree-model.js for why their ids stay unnamespaced).
// Middle     = the MAIN (UI_VISION §8.0) — the same shape as Catalog and Product:
//              a header row (breadcrumb + db_id badge + linked count) over the
//              SHARED folder grid (folder-cols.jsx). Because the tree is
//              COMPOSITE (catalog categories AND products), the grid renders
//              mixed rows — which is exactly why the folder grid had to be
//              genuinely shared before this screen could exist. EDITABLE
//              (Alex, 2026-07-20): Tag/Description save through the SAME
//              routes the source screens use, routed per row by the p:/c:
//              namespace + row type (see handleRowSave). Single click = select
//              (right panel follows a leaf); double click = drill — same
//              semantics as the other screens.
// Right      = detail panel (item name + ref + BOM via the shared OnshapeBomPanel).
//
// NO Onshape API calls anywhere in this file. The three fetches below
// (`/api/catalog/tree`, `/api/products/tree`, `/api/onshape/linked`) are pure DB
// reads; OnshapeLink only builds a cad.onshape.com URL string, and OnshapeBomPanel
// only calls the live API on an explicit user click (Prepare→Load guardrail).

import { useState, useEffect, useMemo, useCallback } from 'react';
import HierarchyWorkspace from './components/HierarchyWorkspace.jsx';
import AssistantSection from './components/AssistantSection.jsx';
import Notice from './components/Notice.jsx';
import ViewSwitcher from './components/ViewSwitcher.jsx';
import DataGrid from './components/DataGrid.jsx';
import OnshapeBomPanel from './components/OnshapeBomPanel.jsx';
import OnshapeSection from './components/OnshapeSection.jsx';
import Card from './components/Card.jsx';
import CatalogView, { catNodeIdOfLeaf } from './CatalogView.jsx';
import ProductsView from './ProductsView.jsx';
import { makeFolderCols } from './folder-cols.jsx';
import { isLeafNode, composeTree, findNode as findNodeById, findPath as findPathTo } from './tree-model.js';
import { dbHrefFor } from './nav-link.js';

import { PANE_GAP, CARD_RADIUS } from './theme-tokens.js';

// Status tally over a PRUNED subtree — recomputed here over the linked-only
// subset (the source trees' rollup `counts` were dropped in pruneTree because
// they cover the full subtree). Product leaves contribute their own status +
// error_count; a linked family contributes its size lines' statuses. `linked`
// counts kept leaves (they are all linked by construction) and feeds
// StatusCounts' ↗ slot; `total` gates the muted-dash empty state.
function pruneStats(node, acc) {
  if (isLeafNode(node)) {
    acc.total += 1;
    acc.linked += 1;
    // Product leaves and family leaves both carry ONE status of their own
    // (product vocabulary / catalog rollup respectively) — tally it either way.
    const s = node.status || 'draft';
    acc[s] = (acc[s] || 0) + 1;
    if (node.isProduct) acc.errors += node.error_count || 0;
    return acc;
  }
  for (const child of node.children || []) pruneStats(child, acc);
  return acc;
}

function statsOf(node) {
  return pruneStats(node, { total: 0, linked: 0, errors: 0 });
}

// Grid rows for one level of the pruned tree — mixed category/leaf rows, the
// same field names the shared folder grid reads (folder-cols.jsx).
function rowsForChildren(children) {
  return (children || []).map((node, i) => {
    if (isLeafNode(node)) {
      return {
        id: node.id, nodeId: node.id, _seq: i + 1,
        _type: node.isProduct ? 'product' : 'family',
        code: node.code, label: node.label,
        status: node.status, error_count: node.error_count || 0,
        // A family leaf's Items = its size count (`sizeCount` from the catalog
        // tree — family nodes come without children).
        count: node.isProduct ? (node.children || []).length : (node.sizeCount ?? 0),
        onshape_ref: node.onshape_ref || null,
      };
    }
    const stats = statsOf(node);
    return {
      id: node.id, nodeId: node.id, _seq: i + 1,
      _type: 'category',
      code: node.code, label: node.label,
      count: (node.children || []).length,
      counts: stats,
      onshape_ref: null,
    };
  });
}

// Right-hand detail panel — identity header (code + label) over the SHARED
// OnshapeSection (components/OnshapeSection.jsx, UI_VISION §8 step 1) in its
// "shrink" density (§9's lego shrink block — a flag, not a second block).
// linkTarget='both': from this screen the useful clicks are Onshape (the CAD)
// AND the item's own editable screen in the DB (deep link `/?view=…&node=…`).
// Fills the HierarchyWorkspace-owned resizable slot (no width of its own).
// BOM fetch — REAL takeoff (Spike_OnshapeTakeoff_Brief.md, slice 3a): one live
// Onshape read per part studio/assembly, cached server-side so a repeat "Load
// BOM" click is 0 additional API calls. Explicit button (no auto-fetch on
// selection) — no ghost calls to the live Onshape API. The Prepare→Load logic
// itself lives in the SHARED OnshapeBomPanel (Spike_OnshapePanelMode_Brief.md).
function OnshapeDetailPanel({ item }) {
  // 2026-08-20: the right region is now a STACK of independent cards, same
  // discipline the middle panel already uses (first, second, next — no
  // shell-owned wrapper card any more). The navy bar stays its own chrome
  // chip, not nested inside anything; everything below is real content, so
  // each piece gets its own white Card, including OnshapeSection's own —
  // which can go back to its normal `frame`/title now that there is no
  // longer one shared card it would have doubled.
  return (
    <>
      <div style={{
        padding: '8px 12px', background: 'var(--th-bar-bg)', color: 'var(--th-bar-text)',
        fontSize: 'var(--fs-small)', fontWeight: 700, letterSpacing: 0.3,
        borderRadius: CARD_RADIUS, border: '1px solid var(--th-border-subtle)', marginBottom: 12,
      }}>
        Onshape
      </div>
      {!item ? (
        <div style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)', padding: '4px 2px' }}>
          Select a linked item.
        </div>
      ) : (
        <>
          <Card storageKey="onshape:section:identity" title={item.dbId?.startsWith('prod:') ? 'Product' : 'Family'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontFamily: 'var(--ff-mono)', fontWeight: 700, fontSize: 'var(--fs-body)', color: 'var(--th-text-body)' }}>{item.code}</span>
            </div>
            <div style={{ fontSize: 'var(--fs-body)', color: 'var(--th-text-body)' }}>{item.label}</div>
          </Card>
          <OnshapeSection
            onshapeRef={item.onshape_ref}
            dbId={item.dbId}
            linkTarget="both"
            dbHref={item.dbHref}
            density="shrink"
            showRef
            storageKey="onshape-screen"
          />
          {/* BOM/takeoff — OnshapeBomPanel renders no chrome of its own by
              design; every caller wraps it. The resolved-cost COMPONENTS
              list lives on the embedded product Main instead — this is the
              raw Onshape read, a different thing. */}
          <Card storageKey="onshape:section:bom" title="BOM">
            <OnshapeBomPanel item={item} />
          </Card>
        </>
      )}
    </>
  );
}

// Embedded Mains (UI_VISION §8.0 realized, Alex 2026-07-20: "the request was
// to have the main panel like the catalog or product based on tree selection").
// For a catalog L3/FAMILY or a PRODUCT leaf, the Onshape Main IS the real
// screen — CatalogView / ProductsView rendered with their tree hidden
// (hideNav), seeded from the Onshape tree selection. Zero forked grid code:
// same columns, same editors, same panels, because it is the same component.
// Each wrapper owns the embed's internal navigation so drilling around inside
// it never fights the Onshape tree; a new tree selection remounts it (key).
// Embedded — ONE generic wrapper (commonization item 4, Alex OK 2026-07-20;
// was EmbeddedCatalog + EmbeddedProduct, identical apart from which screen and
// which extra props). Owns the embed's own local navigation (`sel`, seeded
// from the Onshape tree selection) so drilling around inside it never fights
// the Onshape tree; a new tree selection remounts it via the caller's `key`.
function Embedded({ Screen, nodeId, screenProps }) {
  const [sel, setSel] = useState(nodeId);
  return <Screen hideNav selectedNodeId={sel} onSelectNode={setSel} {...screenProps} />;
}

export default function OnshapeView({
  view,
  setView,
  activeProjectId = '',
  onNavigateToProduct,
  onNavigateToPlacement,
  onNavigateToCatalog,
} = {}) {
  const [catalogTree, setCatalogTree] = useState([]);
  const [productTree, setProductTree] = useState([]);
  const [linked, setLinked] = useState(null);
  // Namespaced tree node id (p:/c: + source scheme). Remembered across visits
  // like the Catalog/Products screens (Alex, 2026-07-20) — a stale id (item
  // unlinked/deleted since) simply resolves to no node and the root grid shows.
  const [selected, setSelected] = useState(() => {
    try { return localStorage.getItem('inoviu:onshapeNodeId') || null; } catch { return null; }
  });
  const [selectedRowId, setSelectedRowId] = useState(null); // grid row highlight (single-click)
  const [editRowId, setEditRowId] = useState(null); // grid row in edit mode
  const [error, setError] = useState(null);
  // ERR-1 — a SAVE failure must not be reported through `error`: that state
  // replaces the whole screen (see the render below), which is right for a failed
  // LOAD and wrong for a rejected edit. That mismatch is why this file reached for
  // alert(). A separate action error renders as a Notice and leaves the page up.
  const [actionError, setActionError] = useState(null);

  // One reload for the three DB reads — mount, and after every save (an edit
  // here writes into the catalog/product tables, so both trees re-derive).
  // ⚠ LOADED IS NOT THE SAME AS NON-EMPTY, and this screen is the one where the
  // difference shows. `TreeNav` renders "Loading…" for an empty tree unless the
  // caller says the fetch has RETURNED (`loading={false}`) — opt-in, and only
  // ProjectsView had opted in. This tree is `linkedOnly`, so on the demo
  // database — where the scrub blanks every onshape_ref — it is legitimately
  // empty, and it span forever on the public site while the grid beside it
  // correctly said "Nothing Onshape-linked beneath this node."
  // Alex, 2026-08-27, with a screenshot of exactly that.
  const [treeLoaded, setTreeLoaded] = useState(false);

  const reload = useCallback(() => {
    const getJson = (url) => fetch(url).then(res => { if (!res.ok) throw new Error(`HTTP ${res.status} (${url})`); return res.json(); });
    return Promise.all([
      getJson('/api/catalog/tree'),
      getJson('/api/products/tree'),
      getJson('/api/onshape/linked'),
    ])
      .then(([catTree, prodTree, linkedData]) => {
        setCatalogTree(catTree);
        setProductTree(prodTree);
        setLinked(linkedData);
      })
      .catch(err => setError(err.message))
      // FINALLY, not .then — a failed load has also finished loading, and leaving
      // the tree spinning behind an error banner says two different things at once.
      .finally(() => setTreeLoaded(true));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // The composite, linked-only, namespaced tree — composeTree() in full (the
  // "leader" configuration; Catalog/Products consume the same function in
  // degenerate single-source form, see tree-model.js).
  const tree = useMemo(
    () => composeTree({ catalog: catalogTree, products: productTree, linkedOnly: true, linked }),
    [catalogTree, productTree, linked]
  );

  // Path to the selected node — breadcrumb + "which level does the grid show".
  const selectedPath = useMemo(
    () => (selected ? findPathTo(tree, selected) : null),
    [selected, tree]
  );
  const selectedNode = selectedPath ? selectedPath[selectedPath.length - 1] : null;
  const selectedIsLeaf = !!(selectedNode && isLeafNode(selectedNode));

  // Which REAL Main does this selection get? (null = the pruned folder grid —
  // category levels, where the folder grid already IS what the source screens
  // show.) Catalog family → its L3's Main filtered to the family (the same
  // landing a tree click on the family gives on the Catalog screen).
  // EVERY node opens the real screen for its side — category levels too (Alex,
  // 2026-09-30: "Normally that should be exactly same as catalog or product based
  // on the selection... It just a different call"). Product categories and catalog
  // L1/L2 used to fall through to this file's own older header + grid, which never
  // got the card layout or the category's 3D Model section, and read as a truncated
  // copy of the real page. Only the ROOT keeps the composite grid below: it mixes
  // both sides, so there is no single real screen for it.
  const embed = useMemo(() => {
    if (!selectedNode) return null;
    if (selectedNode._side === 'c') {
      if (selectedNode.isFamily) {
        return { kind: 'catalog', nodeId: catNodeIdOfLeaf(selectedNode._srcId), familyId: selectedNode._famId ?? null };
      }
      return { kind: 'catalog', nodeId: selectedNode._srcId, familyId: null };
    }
    return { kind: 'product', nodeId: selectedNode._srcId };
  }, [selectedNode]);

  // Tree select: a leaf selection lands like a CLICK on its row — the grid shows
  // the leaf's SIBLINGS with the row highlighted (same as Catalog, where a family
  // leaf filters/highlights inside its L3's grid rather than opening a new level).
  const handleSelectNode = useCallback((id) => {
    setSelected(id ?? null);
    setSelectedRowId(null);
    setEditRowId(null);
    try {
      if (id) localStorage.setItem('inoviu:onshapeNodeId', id);
      else localStorage.removeItem('inoviu:onshapeNodeId');
    } catch { /* private mode etc. — selection just won't persist */ }
  }, []);

  // Grid rows: children of the selected category (roots when nothing selected);
  // for a selected leaf, its parent's children (the leaf's own row among them).
  const gridRows = useMemo(() => {
    if (!selectedNode) return rowsForChildren(tree);
    if (selectedIsLeaf) {
      const parent = selectedPath.length >= 2 ? selectedPath[selectedPath.length - 2] : null;
      return rowsForChildren(parent ? parent.children : tree);
    }
    return rowsForChildren(selectedNode.children);
  }, [selectedNode, selectedIsLeaf, selectedPath, tree]);

  // The SHARED folder grid (folder-cols.jsx): mixed category/leaf rows
  // ('auto'), compact ↗ column. EDITABLE (Alex, 2026-07-20) — Tag/Description
  // save through the same routes the source screens use, routed per row below.
  const gridCols = useMemo(() => makeFolderCols({
    seqKey: '_seq',
    editable: true,
    items: { value: row => row.count },
    statusMode: 'auto',
    onshapeCol: true,
  }), []);

  // Save routing — by SIDE (the p:/c: namespace) and row type, onto the exact
  // PATCH routes the Catalog/Products screens already use. The one hole is a
  // family TAG: l4 rename is a cascading operation (renameLineL4, collision
  // guards, code-group rewrite) with no family-level route — sending it to the
  // families PATCH would silently drop it (FAMILY_EDITABLE_FIELDS), so say so.
  const handleRowSave = useCallback(async (rowId, field, value) => {
    const node = findNodeById(tree, rowId);
    if (!node || (field !== 'code' && field !== 'label')) return;
    try {
      let url;
      if (node.isProduct) {
        url = `/api/products/${node._productId}`;
      } else if (node.isFamily) {
        if (field === 'code') throw new Error('Rename a family Tag on the Catalog screen — it cascades all of its line codes.');
        url = `/api/catalog/families/${node._famId}`;
      } else if (node._side === 'p') {
        url = `/api/products/categories/${node._catId}`;
      } else {
        url = `/api/catalog/categories/${node._catId}`;
      }
      const res = await fetch(url, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
      await reload();
    } catch (err) {
      setActionError(err.message);
    }
  }, [tree, reload]);

  // Right panel follows the selected LEAF — from the grid highlight when it sits
  // on a leaf row, else from a leaf selected in the tree. dbId/dbHref feed the
  // shared OnshapeSection: the badge and the "Open in DB" deep link back to the
  // item's own editable screen (`/?view=…&node=…`, same pathway as panel mode).
  const selectedItem = useMemo(() => {
    const rowNode = selectedRowId ? findNodeById(tree, selectedRowId) : null;
    const node = (rowNode && isLeafNode(rowNode)) ? rowNode : (selectedIsLeaf ? selectedNode : null);
    if (!node) return null;
    return {
      code: node.code, label: node.label, onshape_ref: node.onshape_ref,
      dbId: node.isProduct ? `prod:${node._productId}` : `fam:${node._famId}`,
      // Deep link carries the SOURCE-scheme id (_srcId) — the p:/c: namespace
      // exists only inside this screen's composite tree.
      dbHref: dbHrefFor(node.isProduct ? 'products' : 'catalog', node._srcId),
      productId: node.isProduct ? node._productId : null,
    };
  }, [selectedRowId, selectedIsLeaf, selectedNode, tree]);


  const linkedCount = linked ? (linked.products || []).length + (linked.families || []).length : 0;

  return (
    <HierarchyWorkspace
      rightBottom={<AssistantSection />}
      treeNodes={tree}
      selectedNodeId={selected}
      onSelectNode={handleSelectNode}
      treeHeader={<ViewSwitcher view={view} setView={setView} onSelectRoot={() => handleSelectNode(null)} />}
      filterPlaceholder="Filter linked items…"
      treeLoading={!treeLoaded}
      // Says the same thing as the grid's own empty message, because it is the
      // same fact: nothing here points at a 3D model yet.
      treeEmptyMessage="Nothing is linked to a 3D model yet."
      treeSideIcons
      storageKey="onshape"
      mainStyle={embed
        // An embedded screen brings its own header/breadcrumb + padding — the
        // outer main just hosts it edge to edge.
        ? { display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }
        : { display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: `${PANE_GAP}px ${PANE_GAP}px 0` }}
      rightPanel={<OnshapeDetailPanel item={selectedItem} />}
      mainHeader={embed ? undefined : (
        <div style={{ flexShrink: 0 }}>
          {/* Screen-level line: the hint (nothing selected) and the linked count,
              which is about the SCREEN, not the selected item — so it stays out
              of the header row rather than becoming a fake column. Same move the
              breadcrumb retirement made elsewhere: counts go to a toolbar line,
              identity goes to the row. */}
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, minHeight: 22 }}>
            {!selected && (
              <span style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)' }}>Onshape-linked items — select a node, or drill from the grid.</span>
            )}
            <span style={{ color: 'var(--th-text-secondary)', fontSize: 'var(--fs-small)', marginLeft: 'auto' }}>
              {linkedCount} linked item{linkedCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    >
      {actionError && (
        <Notice onDismiss={() => setActionError(null)}>{actionError}</Notice>
      )}
      {error ? (
        <div style={{ color: 'var(--th-status-deprecated)', fontSize: 'var(--fs-body)' }}>⚠ {error}</div>
      ) : embed ? (
        // THE REAL MAIN for this selection (UI_VISION §8.0): the actual
        // Catalog/Products screen, tree hidden, keyed by the Onshape selection
        // so a new tree click reseeds it.
        embed.kind === 'catalog' ? (
          <Embedded
            key={selected}
            Screen={CatalogView}
            nodeId={embed.nodeId}
            screenProps={{
              initialFamilyId: embed.familyId,
              activeProjectId,
              onNavigateToProduct,
              onNavigateToPlacement,
            }}
          />
        ) : (
          <Embedded
            key={selected}
            Screen={ProductsView}
            nodeId={embed.nodeId}
            screenProps={{ onNavigateToCatalog, onNavigateToPlacement }}
          />
        )
      ) : (
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, paddingBottom: 24 }}>
          <DataGrid
            key={(selectedIsLeaf ? (selectedPath[selectedPath.length - 2]?.id ?? 'root') : selected) || 'root'}
            rows={gridRows}
            columns={gridCols}
            onRowClick={row => {
              setSelectedRowId(row.id === selectedRowId ? null : row.id);
            }}
            onRowDoubleClick={row => handleSelectNode(row.nodeId)}
            selectedRowId={selectedRowId ?? (selectedIsLeaf ? selected : null)}
            onSave={handleRowSave}
            onEditRow={setEditRowId}
            editRowId={editRowId}
            // Auto-edit (2026-08-22, Alex: "switch everything to auto edit") — single
            // click here only ever toggled a bare highlight; drilling in is on
            // double-click (handleSelectNode), unaffected.
            editOnRowClick
            emptyMessage="Nothing Onshape-linked beneath this node."
            storageKey="onshape-main"
          />
        </div>
      )}
    </HierarchyWorkspace>
  );
}
